<template>
  <svg :id="'comp-chart-'+id">
    <!-- Draw an invisible color swatch and tooltip. -->
    <rect class="swatch" x="0" y="0" fill="white"></rect>
    <text class="tooltipt" x="3" y="0"></text>

    <!-- Draw a gray rectangle behind the main graph, to give the illusion of a
    solid 3px border. -->
    <rect class="borderr" x="0" width="100%" fill="#e5e5e5"></rect>

    <g class="blocksg"></g>
  </svg>
</template>

<script>
  import * as d3 from 'd3'
  import * as compat from '@/components/mixins/compat'
  import * as geometry from '@/components/mixins/geometry'
  import { sprintSmallPercent } from '@/components/mixins/util'

  function comp(data) {
    let out = [],
      acc = 0,
      total = data.reduce(function(n, d) { return n + d.count}, 0)

    for (let i = 0; i < data.length; i++) {
      out.push({
        data: data[i],
        index: i,
        start: acc/total,
        end: (acc+data[i].count)/total,
      })
      acc += data[i].count
    }

    return out
  }
  function min(x) {
    return (x < 0 ? 0 : x)
  }

  export default {
    name: 'CompGraph',
    props: {
      id: {type: String, required: true},
      data: {type: Array, required: true},
      select: {type: Function, default: null},

      unit: {type: String, default: 'certs'},
      palette: {type: Function, default: null},
    },
    data: function() {
      let timer = null, self = this
      // debounceResize re-draws the CompGraph when the window resizes, with a
      // 50ms debounce to minimize lag.
      let debounceResize = function() {
        clearTimeout(timer)
        timer = setTimeout(self.resize, 50)
      }

      let color = d3.scaleOrdinal(['#98abc5', '#8a89a6', '#7b6888', '#6b486b', '#a05d56', '#d0743c', '#ff8c00'])
      if (this.palette != null) {
        color = this.palette
      }
      return {
        debounceResize: debounceResize,

        blocksHeight: 0,
        blocksWidth: 0,
        color: color,
      }
    },
    methods: {
      resize: function() {
        let svg = d3.select('svg#comp-chart-'+this.id),
          swatch = svg.select('rect.swatch'),
          tooltip = svg.select('text.tooltipt'),
          g = svg.select('g.blocksg')

        // The tooltip must have text for getBBox below to work.
        let tooltipText = tooltip.text()
        tooltip.attr('fill', 'white').text('Loading...')

        // Get the size of the whole image 'clientRect' and the size of the
        // tooltip 'rect'.
        let clientRect = svg._groups[0][0].getBoundingClientRect(),
          rect = tooltip._groups[0][0].getBBox()

        let tooltipDy = -rect.y
        if (tooltip.attr('dy') != null) {
          tooltipDy += parseFloat(tooltip.attr('dy'))
        }

        // Clean up positioning of swatch and tooltip with info from above.
        swatch.attr('width', rect.height).attr('height', rect.height)
        tooltip.attr('dx', rect.height).attr('dy', tooltipDy)
          .text(tooltipText).attr('fill', 'black')
        rect.height += 3

        svg.select('rect.borderr')
          .attr('y', rect.height).attr('height', clientRect.height-rect.height)
        g.attr('transform', 'translate(3, ' + (3+rect.height) + ')')

        // Resize and re-position all of the already-drawn blocks.
        let blocksHeight = clientRect.height - rect.height - 3*2,
          blocksWidth = clientRect.width - 3*2

        g.selectAll('rect.block')
          .attr('height', blocksHeight)
          .attr('width', function(d) { return min(d.end - d.start) * blocksWidth })
          .attr('x', function(d) { return d.start * blocksWidth })

        this.blocksHeight = blocksHeight, this.blocksWidth = blocksWidth
      },
      draw: function() {
        let select = this.select,
          unit = this.unit,
          blocksHeight = this.blocksHeight,
          blocksWidth = this.blocksWidth,
          color = this.color

        function key(d) {
          return d.data.name
        }
        function blockTween(d) {
          let node = this, i = d3.interpolate(this._current, d)
          node._current = i(0)
          return function(t) {
            let u = i(t)
            node.setAttribute('x', u.start * blocksWidth)
            node.setAttribute('width', min(u.end - u.start) * blocksWidth)
          }
        }

        let svg = d3.select('svg#comp-chart-'+this.id),
          swatch = svg.select('rect.swatch'),
          tooltip = svg.select('text.tooltipt'),
          blocks = svg.select('g.blocksg').selectAll('rect.block')

        let data0 = blocks.data(), data1 = comp(this.data)

        blocks = blocks.data(data1, key)

        blocks.enter().append('rect').attr('class', 'block')
          .each(function(d, i) { this._current = geometry.findBlockEntry(i, data0, data1, key) || d })
          .attr('height', blocksHeight)
          .attr('fill', function(d) { return color(d.data.name) })
          .on(compat.mouseover(), function(d) {
            swatch.attr('fill', color(d.data.name))

            let spacer = '\u00A0\u00A0\u00A0\u2022\u00A0\u00A0\u00A0'
            tooltip.text(d.data.name + ' '
              + '(' + sprintSmallPercent(d.data.percent) + ')'
              + '\u00A0\u00A0\u00A0-\u00A0\u00A0\u00A0'
              + d.data.count.toLocaleString() + ' ' + unit)
          })
          .on(compat.click(), function(d) {
            if (d.data.percent == 1 || select == null) {
              return
            }
            select(d.data.image)
            svg.select('rect.swatch').attr('fill', 'white')
            svg.select('text.tooltipt').text('')
          })
        .merge(blocks).transition().duration(750).tween('block', blockTween)
          .style('cursor', function(d) { return (d.data.percent == 1 || select == null ? 'default' : 'pointer') })

        blocks.exit()
          .datum(function(d, i) { return geometry.findBlockExit(i, data0, data1, key) || d })
          .transition().duration(750).tween('block', blockTween)
          .remove()
      },
    },

    watch: {
      data: function() { this.draw() },
    },
    mounted: function() {
      let svg = d3.select('svg#comp-chart-'+this.id)
      svg.on(compat.mouseleave(), function(d) {
        svg.select('rect.swatch').attr('fill', 'white')
        svg.select('text.tooltipt').text('')
      })

      this.resize()
      this.draw()
      window.addEventListener('resize', this.debounceResize)
    },
    destroyed: function() {
      window.removeEventListener('resize', this.debounceResize)
    },
  }
</script>

<style scoped>
  svg {
    height: 4em;
    width: 100%;

    /* Prevent browser from asking to save/copy image. */
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }
</style>



// WEBPACK FOOTER //
// src/components/widgets/CompGraph.vue